import { useNavigate, useParams } from '@tanstack/react-router';
import { Lock, Mail } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { MessageThread, type ThreadItem } from '@nucleus-commerce/admin-kit/thread';
import type { ConversationDetail, StaffChatMessage } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { session } from '@/shared/session/session';

import { Composer } from '../components/composer';
import { ContextPane } from '../components/context-pane';
import { EventLine, MessageBody, type PeopleIndex } from '../components/message-body';
import { ThreadHeader } from '../components/thread-header';
import { TypingLine } from '../components/typing-line';
import { VisitorPresenceLine } from '../components/visitor-presence-line';
import { useChatAgents } from '../hooks/use-chat-agents';
import { useChatPresence } from '../hooks/use-chat-presence';
import { useConversation } from '../hooks/use-conversation';
import { useConversationMessages } from '../hooks/use-conversation-messages';
import {
  useConversationAction,
  useMarkConversationRead,
} from '../hooks/use-conversation-mutations';
import { useInboxLive } from '../hooks/use-inbox-live';
import { shopperName, visitorTag } from '../lib/display';
import { useInboxScope } from '../lib/inbox-scope';
import { isTypingTarget } from '../lib/keyboard';
import { flattenThread } from '../lib/thread-cache';

interface Item extends ThreadItem {
  message: StaffChatMessage;
}

function useThreadParams(): { storeId: string; conversationId: string } {
  const params = useParams({ strict: false });
  return { storeId: params.storeId ?? '', conversationId: params.conversationId ?? '' };
}

function Thread({ conversation }: { conversation: ConversationDetail }) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const navigate = useNavigate();
  const live = useInboxLive();
  const scope = useInboxScope();
  const { can } = usePermissions();
  const { me } = session.useSession();
  const myId = me?.user.id;
  const { storeId, id } = conversation;
  const messages = useConversationMessages(storeId, id, live.polling);
  const presence = useChatPresence(storeId);
  const agents = useChatAgents(storeId);
  const markRead = useMarkConversationRead(storeId, id);
  const close = useConversationAction(storeId, id, 'close');
  const reopen = useConversationAction(storeId, id, 'reopen');
  const [contextOpen, setContextOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const readUpTo = useRef<string | null>(null);

  const shopper =
    shopperName(conversation) ??
    t('row.visitor', { tag: visitorTag(conversation.visitor?.id ?? conversation.id) });
  const closed = conversation.status === 'closed';
  const writable = can('conversations.write');
  const ticketEmail = conversation.email ?? conversation.visitor?.email ?? null;

  const { follow, setActive, sendRead } = live;
  useEffect(() => follow(storeId, id), [follow, storeId, id]);
  useEffect(() => {
    setActive(id);
    return () => setActive(null);
  }, [setActive, id]);

  const people = useMemo<PeopleIndex>(() => {
    const names = new Map<string, string>();
    for (const agent of agents.data ?? []) names.set(agent.userId, agent.name);
    for (const member of presence.data?.items ?? []) names.set(member.userId, member.name);
    for (const participant of conversation.participants)
      names.set(participant.userId, participant.name);
    if (conversation.assignee) names.set(conversation.assignee.id, conversation.assignee.name);
    if (me) names.set(me.user.id, me.user.name);
    return {
      name: (userId) =>
        typeof userId === 'string'
          ? (names.get(userId) ?? t('events.teammate'))
          : t('events.someone'),
    };
  }, [agents.data, presence.data, conversation.participants, conversation.assignee, me, t]);

  const flat = useMemo(() => flattenThread(messages.data), [messages.data]);
  const items = useMemo<Item[]>(
    () =>
      flat.map((message): Item => {
        if (message.kind === 'event' || message.authorType === 'system') {
          return {
            id: message.id,
            authorKey: null,
            authorName: '',
            align: 'center',
            createdAt: message.createdAt,
            message,
          };
        }
        if (message.authorType === 'visitor') {
          return {
            id: message.id,
            authorKey: 'visitor',
            authorName: shopper,
            align: 'start',
            createdAt: message.createdAt,
            message,
          };
        }
        if (message.authorType === 'bot') {
          return {
            id: message.id,
            authorKey: 'bot',
            authorName: t('message.bot'),
            align: 'start',
            createdAt: message.createdAt,
            message,
          };
        }
        return {
          id: message.id,
          authorKey: `staff:${message.authorId ?? ''}`,
          authorName: message.author?.displayName ?? people.name(message.authorId),
          avatarUrl: message.author?.avatarUrl ?? null,
          align: 'end',
          tone: message.isInternal ? 'internal' : 'default',
          createdAt: message.createdAt,
          message,
        };
      }),
    [flat, shopper, people, t],
  );

  // Reading the thread marks the shopper's messages read: over the socket when it is live.
  const lastVisitorId = [...flat].reverse().find((message) => message.authorType === 'visitor')?.id;
  useEffect(() => {
    if (!lastVisitorId || readUpTo.current === lastVisitorId || conversation.staffUnreadCount === 0)
      return;
    if (document.visibilityState !== 'visible') return;
    readUpTo.current = lastVisitorId;
    if (!sendRead(id, lastVisitorId)) markRead.mutate(lastVisitorId);
  }, [lastVisitorId, conversation.staffUnreadCount, id, sendRead, markRead]);

  const back = useCallback(() => void navigate(scope.listLink), [navigate, scope.listLink]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Escape') {
        if (isTypingTarget(event.target)) (event.target as HTMLElement).blur();
        else back();
        return;
      }
      if (isTypingTarget(event.target) || !writable) return;
      if (event.key === 'e' && !closed) {
        event.preventDefault();
        close.mutate();
      } else if (event.key === 'a') {
        event.preventDefault();
        setAssignOpen(true);
      } else if (event.key === '/') {
        event.preventDefault();
        const textarea = composerRef.current;
        if (textarea) {
          textarea.focus();
          if (!textarea.value.endsWith('/'))
            textarea.setRangeText('/', textarea.value.length, textarea.value.length, 'end');
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back, close, closed, writable]);

  const composer = !writable ? (
    <p className="text-muted-foreground border-t p-3 text-sm">{t('composer.readOnly')}</p>
  ) : closed ? (
    <div className="flex items-center justify-between gap-2 border-t p-3 text-sm">
      <span className="text-muted-foreground">{t('composer.closed')}</span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => reopen.mutate()}
        disabled={reopen.isPending}
      >
        {t('header.reopen')}
      </Button>
    </div>
  ) : (
    <>
      {conversation.kind === 'ticket' ? (
        <p className="text-muted-foreground flex items-center gap-1.5 border-t px-3 pt-2 text-xs">
          <Mail className="size-3" aria-hidden />
          {ticketEmail
            ? t('composer.ticketEmail', { email: ticketEmail })
            : t('composer.ticketNoEmail')}
        </p>
      ) : null}
      <Composer
        storeId={storeId}
        conversationId={id}
        shopper={shopper}
        draftOrderId={conversation.context.draftOrderId ?? null}
        textareaRef={composerRef}
      />
    </>
  );

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {messages.isError ? (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        ) : (
          <MessageThread<Item>
            items={items}
            header={
              <ThreadHeader
                conversation={conversation}
                title={shopper}
                myId={myId}
                onBack={back}
                onShowContext={() => setContextOpen(true)}
                assignOpen={assignOpen}
                onAssignOpenChange={setAssignOpen}
              />
            }
            renderItem={(item) =>
              item.align === 'center' ? (
                <span className="inline-flex items-center gap-1">
                  {item.message.isInternal ? (
                    <Lock className="size-3" aria-label={t('message.internal')} />
                  ) : null}
                  {/* Other system messages keep their card; only events read as one line. */}
                  {item.message.kind === 'event' ? (
                    <EventLine message={item.message} people={people} />
                  ) : (
                    <MessageBody message={item.message} storeId={storeId} />
                  )}
                  <span className="opacity-70">· {format.time(item.createdAt)}</span>
                </span>
              ) : (
                <MessageBody message={item.message} storeId={storeId} />
              )
            }
            renderMeta={(item) => (
              <>
                <time dateTime={item.createdAt} title={format.utc(item.createdAt)}>
                  {format.time(item.createdAt)}
                </time>
                {item.align === 'end' && !item.message.isInternal ? (
                  <span> · {item.message.readAt ? t('message.read') : t('message.sent')}</span>
                ) : null}
                {item.message.emailedAt ? (
                  <span className="inline-flex items-center gap-0.5">
                    {' · '}
                    <Mail className="size-3" aria-hidden />
                    {t('message.emailed')}
                  </span>
                ) : null}
              </>
            )}
            formatDay={(iso) => format.date(iso, 'full')}
            labels={{
              thread: t('thread.label', { name: shopper }),
              loadOlder: t('thread.loadOlder'),
              jumpToLatest: t('thread.jumpToLatest'),
              internal: t('message.internal'),
            }}
            hasOlder={messages.hasPreviousPage}
            loadingOlder={messages.isFetchingPreviousPage}
            onLoadOlder={() => void messages.fetchPreviousPage()}
            empty={
              messages.isPending ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-2/3" />
                  <Skeleton className="ml-auto h-10 w-1/2" />
                </div>
              ) : (
                <p className="text-muted-foreground py-10 text-center text-sm">
                  {t('thread.empty')}
                </p>
              )
            }
            status={
              <>
                <VisitorPresenceLine
                  storeId={storeId}
                  visitorId={conversation.visitor?.id ?? null}
                  customerId={conversation.customer?.id ?? null}
                  name={shopper}
                />
                <TypingLine conversationId={id} shopper={shopper} />
              </>
            }
            composer={composer}
          />
        )}
      </div>
      <aside
        className="hidden w-80 shrink-0 overflow-y-auto border-l xl:block"
        aria-label={t('context.label')}
      >
        <ContextPane storeId={storeId} conversationId={id} />
      </aside>
      <Sheet open={contextOpen} onOpenChange={setContextOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-sm">
          <SheetHeader className="border-b">
            <SheetTitle>{t('context.label')}</SheetTitle>
          </SheetHeader>
          {contextOpen ? <ContextPane storeId={storeId} conversationId={id} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ConversationLoader() {
  const { storeId, conversationId } = useThreadParams();
  const conversation = useConversation(storeId, conversationId);
  if (conversation.isPending) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (conversation.isError)
    return <ErrorState error={conversation.error} onRetry={() => void conversation.refetch()} />;
  return <Thread key={conversation.data.id} conversation={conversation.data} />;
}

/** One conversation: thread with composer, actions in the header, context on the right. */
export function ConversationPage() {
  return (
    <FeatureErrorBoundary name="conversations.thread">
      <ConversationLoader />
    </FeatureErrorBoundary>
  );
}
